/**
 * Browser-side image optimizer.
 *
 * Rules (per product spec, 2026-06-03):
 *   • Reject the SOURCE file if it's larger than 10 MB
 *   • Resize so the longest edge is at most 2000 px (no upscaling)
 *   • Re-encode as optimized JPEG at quality 0.85
 *   • Strip metadata — re-encoding through <canvas> naturally drops EXIF /
 *     XMP / IPTC blocks, which is what we want.
 *   • The original bytes are never uploaded — only the processed File is
 *     returned. Callers should use the returned object for both the presign
 *     `content_type`/`size` payload AND the S3 PUT.
 *
 * The function returns a brand-new `File` whose name ends in `.jpg` and
 * whose MIME type is `image/jpeg`. The caller is expected to revoke any
 * blob URLs they create from it.
 */

export const MAX_SOURCE_BYTES = 10 * 1024 * 1024;   // 10 MB hard cap on the input file
export const MAX_LONG_EDGE_PX = 2000;               // resize cap on the longest dimension
export const OUTPUT_QUALITY = 0.85;                 // JPEG quality 85 — good balance of fidelity vs size

class ImageTooLargeError extends Error {
  constructor(name, size) {
    super(`${name} is ${(size / (1024 * 1024)).toFixed(1)} MB — files must be under 10 MB before optimization.`);
    this.name = "ImageTooLargeError";
  }
}

class ImageDecodeError extends Error {
  constructor(name) {
    super(`${name} could not be decoded — file may be corrupted or use an unsupported encoding.`);
    this.name = "ImageDecodeError";
  }
}

/**
 * Decode a File into an Image element. We use `Image` rather than
 * `createImageBitmap` so we can intentionally orient via EXIF — modern
 * browsers honor image-orientation in the bitmap path automatically when
 * drawn to canvas, so the resulting JPEG comes out upright.
 */
function _decode(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    // CORS isn't relevant — we created the blob URL ourselves.
    img.onload = () => {
      // Free the blob URL the moment we have pixels; the Image object
      // already owns its bitmap data.
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageDecodeError(file.name));
    };
    img.src = url;
  });
}

/**
 * Optimize a File for upload. Throws `ImageTooLargeError` for >10 MB
 * inputs and `ImageDecodeError` if the browser can't read the file.
 *
 * Returns a new `File` (always JPEG) — callers should treat it as
 * unrelated to the input File for all upload purposes.
 */
export async function optimizeImageForUpload(file) {
  if (file.size > MAX_SOURCE_BYTES) {
    throw new ImageTooLargeError(file.name, file.size);
  }

  const img = await _decode(file);
  const { naturalWidth: w, naturalHeight: h } = img;
  if (!w || !h) throw new ImageDecodeError(file.name);

  // Compute target dimensions — never upscale, always preserve aspect ratio.
  const longest = Math.max(w, h);
  const scale = longest > MAX_LONG_EDGE_PX ? MAX_LONG_EDGE_PX / longest : 1;
  const targetW = Math.round(w * scale);
  const targetH = Math.round(h * scale);

  // Draw to a 2D canvas — re-encoding strips all metadata (EXIF, XMP, IPTC,
  // ICC profile, color depth quirks). We also fill with white to avoid
  // alpha-channel surprises when the source was a PNG with transparency.
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d", { alpha: false });
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);

  // Encode to JPEG. `toBlob` is async and runs off the main thread on most
  // browsers, so the UI doesn't jank on large images.
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
      "image/jpeg",
      OUTPUT_QUALITY,
    );
  });

  // Rename to `.jpg` — keep the user's original stem for tracing back.
  const stem = file.name.includes(".") ? file.name.slice(0, file.name.lastIndexOf(".")) : file.name;
  const outName = `${stem}.jpg`;
  return new File([blob], outName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

export { ImageTooLargeError, ImageDecodeError };
